//@flow
import * as Immutable from 'immutable';
import {
    validateId,
    validatable,
    validate,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';

import DataUtils from 'common/utils/DataUtils';
import StringUtils from 'common/utils/StringUtils';
import NumberUtils from 'common/utils/NumberUtils';
import RegExUtil from 'common/utils/RegExUtil';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';

import { MasterPersonnel } from 'common/models/flow/MasterPersonnel';
import { PhoneNumber } from 'common/models/internal/PhoneNumber';
import { EmailAddress } from 'common/models/internal/EmailAddress';
import { AuditMetadata } from 'common/models/internal/AuditMetadata';
import { BusinessUnitId } from './BusinessUnits';

import { TeamId } from './TeamId';
import { RoleId } from './RoleId';
import { TransporterId } from './TransporterId';
import { Role } from './Role';

import type { MasterPersonnelModelType } from 'common/models/flow/MasterPersonnel';
import EntityTypeEnum from 'common/enums/EntityTypeEnum';
import { PersonnelTypeEnum } from 'common/enums/PersonnelEnums';

import I18nHelper from 'common/helpers/I18nHelper';
import ClientConfigService from 'common/services/ClientConfigService';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.translate.bind(null, translationsNamespace);

export const PersonnelStatusEnum = new EntityTypeEnum({
    Inactive: 'INACTIVE',
    Active: 'ACTIVE'
});

export const AccessTypeEnum = new EntityTypeEnum({
    READ: 'READ',
    WRITE: 'WRITE'
});

export const validateBoolean = (value: boolean, property: string) => {
    if (DataUtils.isEmpty(value) && !DataUtils.isBoolean(value)) {
        return nsTranslate('entityValidationErrors.empty', {
            fieldType: nsTranslate(property)
        });
    }
    if (!DataUtils.isBoolean(value)) {
        return nsTranslate('validations:booleanValueValidation', {
            positiveLabel: nsTranslate(
                'entityManagement:masterPersonnelForm.boolean.yes'
            ),
            negativeLabel: nsTranslate(
                'entityManagement:masterPersonnelForm.boolean.no'
            )
        });
    }
};

@validatable
class InternalMasterPersonnel extends MasterPersonnel {
    static fromJS(json: MasterPersonnelModelType): InternalMasterPersonnel {
        const state: Object = Object.assign({}, json);
        state.email = EmailAddress.fromJS(state.email);
        state.phone = state.phone
            ? PhoneNumber.fromJS(state.phone)
            : state.phone;
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.roleMasterId = RoleId.fromJS(state.roleMasterId);
        state.roleMaster = state.roleMaster
            ? Role.fromJS(state.roleMaster)
            : state.roleMaster;
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.customProperties = Immutable.Map(state.customProperties);
        state.businessUnitIds = Immutable.List(
            state.businessUnitIds
        ).map(item => BusinessUnitId.fromJS(item));
        state.transporters = Immutable.List(state.transporters).map(item =>
            TransporterId.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    get email(): EmailAddress {
        return this._state.get('email');
    }

    get phone(): ?PhoneNumber {
        return this._state.get('phone');
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    setHiddenModelProperties(hiddenModelProperties: Immutable.List<string>) {
        return this.clone(
            this._state.set('hiddenModelProperties', hiddenModelProperties)
        );
    }

    getHiddenModelProperties(): Immutable.List<string> {
        return this._state.get('hiddenModelProperties');
    }

    isHidden(key: string): boolean {
        const hiddenModelProperties = this.getHiddenModelProperties();
        const [groupKey] = key.split('.');
        return (
            (hiddenModelProperties && hiddenModelProperties.includes(key)) ||
            (hiddenModelProperties && hiddenModelProperties.includes(groupKey))
        );
    }

    @validateAll
    setAllValidationErrors(): this {
        return this;
    }

    getCellValidationMessage({ header, validationData }: Object) {
        return EntityImportDataHelper.getCellValidationMessage({
            entity: this,
            header,
            validationData
        });
    }

    @validate
    setId(id: string): this {
        return super.setId(id);
    }

    validateId() {
        return validateId(this.id);
    }

    @validate
    setCode(code: string): this {
        return super
            .setCode(code)
            .setId(!DataUtils.isEmpty(code) ? RegExUtil.conformToId(code) : '');
    }

    validateCode() {
        const code = this.code || this.id;
        if (StringUtils.isEmpty(code)) {
            return nsTranslate('entityValidationErrors.codeEmpty');
        }
        if (code.trim().length < 2) {
            return nsTranslate('entityValidationErrors.codeLessThan2');
        }
        if (code.trim().length > 32) {
            return nsTranslate('entityValidationErrors.codeGreaterThan32');
        }
    }

    @validate
    setName(name: string): this {
        return super.setName(name);
    }

    validateName() {
        if (!this.name || this.name.trim().length === 0) {
            return nsTranslate('masterPersonnelValidations.nameEmpty');
        }

        if (!this.name || this.name.length > 128) {
            return nsTranslate('masterPersonnelValidations.nameOverLimit');
        }
    }

    @validate
    setPhone(phoneNumber: ?PhoneNumber): this {
        return super.setPhone(phoneNumber);
    }

    validatePhone(): ?string {
        const { phone } = this;
        const { phoneNumber } = phone || {};
        if (phoneNumber && phoneNumber.trim().length > 16) {
            return nsTranslate('validations:phoneNumber.lengthGreaterThan16');
        }
        if (
            !DataUtils.isEmpty(phoneNumber) &&
            !NumberUtils.isNumeric(phoneNumber)
        ) {
            return nsTranslate('validations:phoneNumber.invalidPhoneNumber');
        }
    }

    @validate
    setEmail(emailAddress: EmailAddress): this {
        return super.setEmail(emailAddress);
    }

    validateEmail(): ?string {
        const { email } = this;
        if (DataUtils.isEmpty(email?.emailAddress)) {
            return nsTranslate('masterPersonnelValidations.emailAddressEmpty');
        }
        return this.email.validateEmailAddress();
    }

    @validate
    setPersonnelType(personnelType: PersonnelTypeEnum): this {
        return super.setPersonnelType(personnelType);
    }

    validatePersonnelType(): ?string {
        const { personnelType } = this;
        const fieldType = nsTranslate(
            'masterPersonnelProperties.personnelType'
        );
        if (DataUtils.isEmpty(personnelType)) {
            return nsTranslate('entityValidationErrors.empty', {
                fieldType
            });
        }
    }

    @validate
    setTransporters(transporters: Immutable.List<TransporterId>): this {
        return super.setTransporters(transporters);
    }

    validateTransporters(): ?string {
        if (this.personnelType !== PersonnelTypeEnum.TRANSPORTER) {
            return null;
        }
        if (this.transporters.size === 0) {
            return nsTranslate('entityValidationErrors.empty', {
                fieldType: nsTranslate(
                    'masterPersonnelProperties.transporterId'
                )
            });
        }
    }

    @validate
    setHasAllTeamsAccess(hasAllTeamsAccess: boolean): this {
        return super.setHasAllTeamsAccess(hasAllTeamsAccess);
    }

    validateHasAllTeamsAccess(): ?string {
        if (ClientConfigService.isRbacEnabled()) {
            return null;
        }

        if (this.personnelType === PersonnelTypeEnum.TRANSPORTER) {
            return null;
        }

        return validateBoolean(
            this.hasAllTeamsAccess,
            'masterPersonnelProperties.hasAllTeamsAccess'
        );
    }

    @validate
    setTeams(teams: Immutable.List<TeamId>): this {
        return super.setTeams(teams);
    }

    validateTeams(): ?string {
        if (this.personnelType === PersonnelTypeEnum.TRANSPORTER) {
            return null;
        }
        const { hasAllTeamsAccess, teams } = this;
        const fieldType = nsTranslate('masterPersonnelProperties.teams');
        if (!hasAllTeamsAccess && !teams.size) {
            return nsTranslate('entityValidationErrors.empty', {
                fieldType
            });
        }
    }

    @validate
    setCanManageEntities(canManageEntities: boolean): this {
        return super.setCanManageEntities(canManageEntities);
    }

    validateCanManageEntities(): ?string {
        if (ClientConfigService.isRbacEnabled()) {
            return null;
        }
        return validateBoolean(
            this.canManageEntities,
            'masterPersonnelProperties.canManageEntities'
        );
    }

    @validate
    setCanVerifyEntities(canVerifyEntities: boolean): this {
        return super.setCanVerifyEntities(canVerifyEntities);
    }

    validateCanVerifyEntities(): ?string {
        if (ClientConfigService.isRbacEnabled()) {
            return null;
        }
        return validateBoolean(
            this.canVerifyEntities,
            'masterPersonnelProperties.canVerifyEntities'
        );
    }

    @validate
    setCanManagePersonnel(canManagePersonnel: boolean): this {
        return super.setCanManagePersonnel(canManagePersonnel);
    }

    validateCanManagePersonnel(): ?string {
        if (ClientConfigService.isRbacEnabled()) {
            return null;
        }
        return validateBoolean(
            this.canManagePersonnel,
            'masterPersonnelProperties.canManagePersonnel'
        );
    }

    @validate
    setAccessType(accessType: AccessTypeEnum): this {
        return super.setAccessType(accessType);
    }

    validateAccessType(): ?string {
        if (ClientConfigService.isRbacEnabled()) {
            return null;
        }
        const { accessType } = this;
        const accessTypeValues = AccessTypeEnum.values();
        const fieldType = nsTranslate('masterPersonnelProperties.accessType');
        if (DataUtils.isEmpty(accessType)) {
            return nsTranslate('entityValidationErrors.empty', {
                fieldType
            });
        }
        if (!accessTypeValues.includes(accessType)) {
            const typeList = AccessTypeEnum.getOptions({
                translationKey: 'masterPersonnelProperties.accessTypes'
            })
                .map(({ label }) => label)
                .join('/');
            return nsTranslate('entityValidationErrors.oneOfType', {
                typeList,
                fieldType
            });
        }
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return false;
    }

    sanitize(): this {
        return this;
    }

    defaultSanitize(): this {
        const sanitizePersonnel = super.defaultSanitize();
        if (sanitizePersonnel) {
            const {
                teams,
                hasAllTeamsAccess,
                email,
                phone,
                canManageEntities,
                canVerifyEntities,
                canManagePersonnel,
                accessType
            } = sanitizePersonnel;
            const sanitizedEmailAddress = email?.defaultSanitize();
            const sanitizedPhoneNumber = phone?.defaultSanitize();
            const sanitizedTeams = hasAllTeamsAccess ? Immutable.List() : teams;
            return sanitizePersonnel
                .setEmail(sanitizedEmailAddress)
                .setPhone(sanitizedPhoneNumber)
                .setTeams(sanitizedTeams)
                .setCanManageEntities(canManageEntities ?? false)
                .setCanVerifyEntities(canVerifyEntities ?? false)
                .setCanManagePersonnel(canManagePersonnel ?? false)
                .setAccessType(accessType ?? AccessTypeEnum.READ);
        }
        return this;
    }

    static defaultInit({ props }): InternalMasterPersonnel {
        const emailAddress = EmailAddress.defaultInit({});
        const phoneNumber = PhoneNumber.defaultInit({});
        return InternalMasterPersonnel.fromJS({})
            .setEmail(emailAddress)
            .setPhone(phoneNumber)
            .setCanManageEntities(false)
            .setCanVerifyEntities(false)
            .setCanManagePersonnel(false)
            .setHasAllTeamsAccess(false)
            .setAccessType(AccessTypeEnum.READ)
            .setPersonnelType(props?.personnelType ?? PersonnelTypeEnum.CLIENT);
    }

    get businessUnitIds(): Immutable.List<BusinessUnitId> {
        return this._state.get('businessUnitIds');
    }

    get roleMasterId(): RoleId {
        return this._state.get('roleMasterId');
    }

    @validate
    setRoleMasterId(roleMasterId: RoleId): this {
        return super.setRoleMasterId(roleMasterId);
    }

    validateRoleMasterId(): ?string {
        if (!ClientConfigService.isRbacEnabled()) {
            return null;
        }
        const { roleMasterId } = this;
        if (!roleMasterId || !roleMasterId.id) {
            return nsTranslate('entityValidationErrors.empty', {
                fieldType: nsTranslate('masterPersonnelProperties.role')
            });
        }
    }

    get transporters(): Immutable.List<TransporterId> {
        return this._state.get('transporters');
    }
}

export { InternalMasterPersonnel as MasterPersonnel };
